import { Directive, ElementRef, Input, AfterViewInit, OnChanges, OnDestroy, HostListener, PLATFORM_ID, Inject, NgZone, SimpleChanges } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Directive({
  selector: '[appWordClamp]'
})
export class WordClampDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('appWordClamp') originalText: string = '';
  @Input() maxLines: number = 2;

  private resizeObserver: any;
  private isBrowser: boolean;
  private lastWidth = 0;
  private lastHeight = 0;
  private lastText = '';

  constructor(
    private el: ElementRef,
    @Inject(PLATFORM_ID) private platformId: any,
    private ngZone: NgZone
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngAfterViewInit() {
    if (this.isBrowser) {
      setTimeout(() => {
        this.clampText(true);
      }, 0);

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.ngZone.runOutsideAngular(() => {
            this.clampText();
          });
        });
        this.resizeObserver.observe(this.el.nativeElement);
      }

      if (typeof document !== 'undefined' && (document as any).fonts) {
        (document as any).fonts.ready.then(() => {
          this.ngZone.runOutsideAngular(() => {
            this.clampText(true);
          });
        });
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.isBrowser && (changes['originalText'] || changes['maxLines'])) {
      setTimeout(() => {
        this.clampText(true);
      }, 0);
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.isBrowser && !this.resizeObserver) {
      this.clampText();
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  // Strips markup (e.g. footnote <sup> tags) so it doesn't interfere with
  // word-count/height measurement during clamping.
  private stripTags(text: string): string {
    return text.replace(/<[^>]*>/g, '');
  }

  private clampText(force = false) {
    const element = this.el.nativeElement;
    const rawText = this.originalText || '';
    const plainText = this.stripTags(rawText);

    if (!plainText) {
      element.innerHTML = '';
      return;
    }

    const currentWidth = element.clientWidth;
    const currentHeight = element.clientHeight;

    // Avoid redundant recalculations unless forced or size/text changed
    if (!force && currentWidth === this.lastWidth && currentHeight === this.lastHeight && rawText === this.lastText) {
      return;
    }

    this.lastWidth = currentWidth;
    this.lastHeight = currentHeight;
    this.lastText = rawText;

    // Set temp inline styles to ensure accurate wrapping measurement
    const prevWordBreak = element.style.wordBreak;
    const prevOverflowWrap = element.style.overflowWrap;
    const prevWhiteSpace = element.style.whiteSpace;

    element.style.wordBreak = 'normal';
    element.style.overflowWrap = 'normal';
    element.style.whiteSpace = 'normal';

    // Measure the exact height limit for the target number of lines
    element.innerText = Array(this.maxLines).fill('A').join('\n');
    const maxHeight = element.scrollHeight + 2; // Include a small subpixel rendering tolerance

    // Restore text to original (plain) to measure actual wrapping
    element.innerText = plainText;

    if (element.scrollHeight > maxHeight) {
      const words = plainText.split(/\s+/);
      let low = 0;
      let high = words.length;
      let bestText = '';

      // Binary search to find the maximum number of words that fit within maxHeight
      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const trialText = words.slice(0, mid).join(' ') + (mid < words.length ? '...' : '');
        element.innerText = trialText;

        if (element.scrollHeight <= maxHeight) {
          bestText = trialText;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      element.innerText = bestText;
    } else {
      // Full text fits without truncation: render with markup intact
      // (e.g. footnote <sup> markers) instead of the plain, measurement-only text.
      element.innerHTML = rawText;
    }

    // Restore inline styles
    element.style.wordBreak = prevWordBreak;
    element.style.overflowWrap = prevOverflowWrap;
    element.style.whiteSpace = prevWhiteSpace;
  }
}
